import Splide from "@splidejs/splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";

class SplideSlider extends HTMLElement {
  splide: any;
  splideOptions: Record<string, unknown> = {};
  _editorBound = false;

  constructor() {
    super();

    this.generateSplide();    
  }

  connectedCallback() {
    this.bindThemeEditorEvents();
  }

  bindThemeEditorEvents() {
    if (this._editorBound) return;
    if (!(window as any).Shopify?.designMode) return;

    this._editorBound = true;
    this.addEventListener("shopify:block:select", this.handleBlockSelect);
    this.addEventListener("shopify:block:deselect", this.handleBlockDeselect);
  }

  handleBlockSelect = (event: Event) => {
    const index = this.resolveRealSlideIndex(event);
    if (index >= 0 && this.splide) {
      this.splide.go(index);
    }
    this.pauseCarouselMotion();
  };

  handleBlockDeselect = () => {
    this.resumeCarouselMotion();
  };

  resolveRealSlideIndex(event: Event): number {
    const slide = (event.target as HTMLElement).closest(".splide__slide") as HTMLElement | null;
    const realSlides = Array.from(this.querySelectorAll(".splide__slide:not(.splide__slide--clone)"));
    let index = slide ? realSlides.indexOf(slide) : -1;

    if (index === -1 && slide) {
      const blockId = slide.getAttribute("data-shopify-editor-block");
      if (blockId) {
        index = realSlides.findIndex(
          (realSlide) => realSlide.getAttribute("data-shopify-editor-block") === blockId
        );
      }
    }

    return index;
  }

  pauseCarouselMotion() {
    this.splide?.Components?.Autoplay?.pause();
    this.splide?.Components?.AutoScroll?.pause();
  }

  // Splide instantiates Autoplay even when autoplay is off, and its play() starts the
  // interval regardless, so resuming blind would set a static carousel moving. Only
  // restart what the section actually configured, after reduced motion has had its say.
  // -- SG 20260810
  resumeCarouselMotion() {
    if (this.splideOptions.autoplay) {
      this.splide?.Components?.Autoplay?.play();
    }

    if (this.splideOptions.autoScroll) {
      this.splide?.Components?.AutoScroll?.play();
    }
  }

  // Disable drag/autoscroll when user prefers reduced motion -- SG 20260703
  applyReducedMotion(options: Record<string, unknown>) {
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    options.drag = false;
    options.speed = 0;

    // Splide's autoplay is separate from the autoScroll extension, so it needs
    // its own opt-out to satisfy WCAG 2.2.2 -- SG 20260729
    options.autoplay = false;

    if (options.autoScroll) {
      delete options.autoScroll;
    }
  }

  generateSplide() {
    const splideObject = this.getAttribute('data-splide');
    const splideClass = this.getAttribute('data-class');
    const splideObjectJsonData = splideObject ? JSON.parse(splideObject) : {};

    this.applyReducedMotion(splideObjectJsonData);
    this.splideOptions = splideObjectJsonData;

    if(!splideClass) return;
    try {
      this.splide = new Splide(`.${splideClass}`, splideObjectJsonData);
    }
    catch{}

    if(this.splide) {
      this.generateSplideProgressBar(this.splide);

      if(splideObjectJsonData.autoScroll) {
        this.splide?.mount({ AutoScroll });  
      }
      else {
        this.splide?.mount();  
      }
    }
  }

  generateSplideProgressBar(splide: any) {
    const splideProgressWrapper = this.querySelector('.progress-bar__wrapper') as HTMLElement;

    if(splideProgressWrapper) {
      const splideProgress = splide.root.querySelector('.splide-bar') as HTMLElement;

      splide.on('mounted move drag updated', function () {
        const splideLength = splide.length;
        const perPage = splide.options.perPage || 1;
  
        if(splideLength <= perPage) {
          splideProgressWrapper.classList.add("hidden");
        }
        else {
          splideProgressWrapper.classList.remove("hidden");
  
          const currentIndex = splide.index;
          const pages = Math.ceil(splideLength / perPage);
          const currentPage = Math.ceil(currentIndex / perPage) + 1;
          splideProgress.style.width = String((currentPage / pages) * 100) + "%"; 
        }
      });
    }
  }
}


customElements.define("section-splide", SplideSlider);
